/**
 * WebView Detection Utility
 *
 * Detects if the app is running in Kiln mobile app WebView or real browser.
 * Used for Google Calendar OAuth flow to guide mobile users to system browser.
 *
 * Implementation follows RFC 8252 best practices and Google OAuth policies.
 *
 * Detection Logic:
 * - Android: Checks for "wv" marker or missing Chrome token
 * - iOS: Checks for missing "Version" token with Safari
 * - Desktop browsers: Never detected as WebView
 */

/**
 * Check if running in Kiln app WebView
 *
 * @returns {boolean} True if in WebView, false if in real browser
 *
 * Detection patterns:
 * - Android WebView: Has "wv" in user agent OR has "Android" but no "Chrome"
 * - iOS WebView: Has "iPhone/iPad" but no "Version" token, OR has "Safari" without "Version"
 * - Real browsers: Always have full browser tokens (Chrome/120.0, Version/16.0, etc.)
 */
export function isKilnAppWebView() {
  // Defensive check for missing navigator (e.g., Node.js environment)
  if (typeof navigator === 'undefined' || !navigator) {
    return false;
  }

  const ua = navigator.userAgent || '';

  // ============================================================================
  // Android WebView Detection
  // ============================================================================

  // Android WebView with "wv" marker is the single most reliable WebView signal;
  // no real browser ever carries it, so it is safe to short-circuit on first.
  // Example: "Mozilla/5.0 (Linux; Android 12; wv) AppleWebKit/537.36"
  if (/wv/.test(ua)) {
    return true;
  }

  // ============================================================================
  // Real Browser Allowlist (return false) — MUST run before the heuristics below
  // ============================================================================
  // These mobile browsers identify themselves with explicit tokens but do NOT
  // carry the tokens the heuristics look for (iOS Chrome/Firefox lack "Version/",
  // Android Firefox lacks "Chrome/"). If the heuristics ran first they would be
  // misclassified as WebViews and wrongly sent through the OAuth deep-link flow.
  if (/CriOS/.test(ua)) {   // iOS Chrome
    return false;
  }
  if (/FxiOS/.test(ua)) {   // iOS Firefox
    return false;
  }
  if (/EdgiOS/.test(ua)) {  // iOS Edge
    return false;
  }
  if (/Firefox/.test(ua)) { // Firefox (Android / desktop)
    return false;
  }

  // ============================================================================
  // Android WebView Detection (heuristic)
  // ============================================================================

  // Android without Chrome token (fallback)
  // Real Chrome always has "Chrome/" token
  // Example WebView: "Mozilla/5.0 (Linux; Android 12) AppleWebKit/537.36 Mobile Safari/537.36"
  if (/Android/.test(ua) && !/Chrome\/[\d.]+/.test(ua)) {
    return true;
  }

  // ============================================================================
  // iOS WebView Detection (heuristic)
  // ============================================================================

  const isIOS = /iPhone|iPad|iPod/.test(ua);

  if (isIOS) {
    // Pattern 1: No Safari token at all (most common WebView)
    // Example: "Mozilla/5.0 (iPhone; CPU iPhone OS 16_0) AppleWebKit/605.1.15 Mobile/15E148"
    if (!/Safari/.test(ua)) {
      return true;
    }

    // Pattern 2: Has Safari but no Version token (WebView with Safari token)
    // Real Safari always has "Version/X.Y"
    // Example WebView: "Mozilla/5.0 (iPhone) AppleWebKit/605.1.15 Safari/604.1"
    // Real Safari: "Mozilla/5.0 (iPhone) AppleWebKit/605.1.15 Version/16.0 Safari/604.1"
    if (/Safari/.test(ua) && !/Version\/[\d.]+/.test(ua)) {
      return true;
    }
  }

  // Desktop Chrome: Has "Chrome" and no "Android" or "Mobile"
  if (/Chrome/.test(ua) && !/Android|Mobile/.test(ua)) {
    return false;
  }

  // Desktop Safari: Has "Safari" and "Version" with "Macintosh"
  if (/Macintosh/.test(ua) && /Safari/.test(ua) && /Version/.test(ua)) {
    return false;
  }

  // Default: Not a WebView (conservative approach)
  return false;
}

/**
 * Get detailed environment information for debugging
 *
 * @returns {Object} Environment information
 * @property {string} userAgent - Full user agent string
 * @property {boolean} isWebView - Whether running in WebView
 * @property {string} platform - Platform identifier
 * @property {string} vendor - Browser vendor
 */
export function getEnvironmentInfo() {
  if (typeof navigator === 'undefined' || !navigator) {
    return {
      userAgent: 'undefined',
      isWebView: false,
      platform: 'undefined',
      vendor: 'undefined'
    };
  }

  return {
    userAgent: navigator.userAgent || '',
    isWebView: isKilnAppWebView(),
    platform: navigator.platform || '',
    vendor: navigator.vendor || ''
  };
}

/**
 * Get browser name for display purposes
 *
 * @returns {string} Human-readable browser name
 */
export function getBrowserName() {
  if (typeof navigator === 'undefined' || !navigator) {
    return 'Unknown Browser';
  }

  const ua = navigator.userAgent || '';

  if (/CriOS/.test(ua)) return 'Chrome (iOS)';
  if (/FxiOS/.test(ua)) return 'Firefox (iOS)';
  if (/EdgiOS/.test(ua)) return 'Edge (iOS)';
  if (/Chrome/.test(ua)) return 'Chrome';
  if (/Firefox/.test(ua)) return 'Firefox';
  if (/Edg/.test(ua)) return 'Edge';
  if (/Safari/.test(ua) && /Version/.test(ua)) return 'Safari';

  return 'Browser';
}

/**
 * Get platform name for display purposes
 *
 * @returns {string} Human-readable platform name
 */
export function getPlatformName() {
  if (typeof navigator === 'undefined' || !navigator) {
    return 'Unknown Platform';
  }

  const ua = navigator.userAgent || '';

  if (/Android/.test(ua)) return 'Android';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Linux/.test(ua)) return 'Linux';

  return 'Unknown Platform';
}

// ============================================================================
// Usage Examples
// ============================================================================

/**
 * Example 1: Check before OAuth
 *
 * if (isKilnAppWebView()) {
 *   // Show modal guiding user to open in browser
 *   showDeepLinkModal();
 * } else {
 *   // Direct OAuth redirect
 *   window.location.href = oauthUrl;
 * }
 *
 * Example 2: Display environment info
 *
 * const info = getEnvironmentInfo();
 * console.log('Environment:', info);
 * // {
 * //   userAgent: "Mozilla/5.0 (Linux; Android 12; wv) ...",
 * //   isWebView: true,
 * //   platform: "Linux armv8l",
 * //   vendor: "Google Inc."
 * // }
 *
 * Example 3: Show user-friendly browser name
 *
 * const browserName = getBrowserName();
 * console.log(`Please open in ${browserName}`);
 * // "Please open in Safari"
 */
